import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Spinner } from "reactstrap"
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from "recharts"
import { setBreadcrumbItems } from "../../../store/actions"
import { getResumenEmisor } from "../api"
import {
  formatNumber, formatPercent, formatFecha, formatFechaHora,
  estadoConfig, bounceConfig, colorPara, rangoDelMes, buildRangoTimestamp,
} from "../helpers"
import HeaderFiltros from "./HeaderFiltros"

const breadcrumbItems = [
  { title: "LS-DA", link: "#" },
  { title: "Envío de correos", link: "#" },
  { title: "Análisis", link: "#" },
]

const ahora = new Date()

// ───────── Componentes UI compartidos ─────────

const StatCard = ({ icon, label, value, accent = "#3b82f6", sub }) => (
  <div style={{
    flex: "1 1 200px", minWidth: 0,
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14,
    padding: "16px 20px",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: `${accent}1a`, color: accent,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 18, flexShrink: 0,
      }}>
        <i className={`mdi ${icon}`} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </span>
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{sub}</div>}
  </div>
)
StatCard.propTypes = { icon: PropTypes.string, label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node }

const SectionCard = ({ title, icon, children, action }) => (
  <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
    <div className="d-flex justify-content-between align-items-baseline mb-2 flex-wrap gap-2">
      <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
        {icon && <i className={`mdi ${icon} me-2 text-primary`} />}
        {title}
      </h6>
      {action}
    </div>
    {children}
  </div>
)
SectionCard.propTypes = { title: PropTypes.node, icon: PropTypes.string, children: PropTypes.node, action: PropTypes.node }

const th = {
  background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
  padding: "8px 10px", textAlign: "left",
  fontSize: 11, color: "#475569", fontWeight: 700,
  textTransform: "uppercase", letterSpacing: 0.4,
}
const td = {
  padding: "8px 10px", borderBottom: "1px solid #f1f5f9",
  fontSize: 13, color: "#0f172a", verticalAlign: "middle",
}

// ───────── Componente principal ─────────

const Analisis = ({ empresaSeleccionada, user, setBreadcrumbItems: setBreadcrumb }) => {
  const [modo, setModo] = useState("mes")
  const [year, setYear] = useState(ahora.getFullYear())
  const [month, setMonth] = useState(ahora.getMonth() + 1)
  const [fechaInicio, setFechaInicio] = useState("")
  const [fechaFin, setFechaFin] = useState("")

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [consultado, setConsultado] = useState(false)

  const nitemisor = empresaSeleccionada?.nit || empresaSeleccionada?.identificacion || ""
  const empresaNombre = empresaSeleccionada?.nombre || ""

  useEffect(() => {
    document.title = "Análisis envíos de correo | LS-DA"
    const greeting = user
      ? `Bienvenido, ${user.nombre || ""} ${user.apellido || ""}`.trim()
      : "Análisis de envíos"
    setBreadcrumb(greeting, breadcrumbItems)
  }, [user, setBreadcrumb])

  useEffect(() => {
    setData(null)
    setError("")
    setConsultado(false)
  }, [nitemisor])

  const construirRango = useCallback(() => {
    if (modo === "mes") {
      const { desde, hasta } = rangoDelMes(year, month)
      return { desde, hasta }
    }
    return buildRangoTimestamp(fechaInicio, fechaFin)
  }, [modo, year, month, fechaInicio, fechaFin])

  const puedeConsultar = useMemo(() => {
    if (!nitemisor) return false
    if (modo === "mes") return Boolean(year && month)
    return Boolean(fechaInicio && fechaFin && fechaInicio <= fechaFin)
  }, [nitemisor, modo, year, month, fechaInicio, fechaFin])

  const cargar = useCallback(async () => {
    if (!nitemisor) return
    setLoading(true)
    setError("")
    try {
      const r = await getResumenEmisor(nitemisor, construirRango())
      const body = r.data
      if (body?.success === false) throw new Error(body.error || body.message || "Error en el servidor")
      setData(body?.data || body)
      setConsultado(true)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "No se pudo cargar el análisis.")
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [nitemisor, construirRango])

  // ───────── Derivar bloques del resumen (deben estar antes de cualquier early return) ─────────

  const emisorInfo = data?.emisor || {}
  const tarjetas = data?.tarjetas || {}
  const engagement = tarjetas?.engagement || data?.engagement || {}
  const eventosSes = data?.eventos_ses || {}
  const porEstado = useMemo(() => data?.por_estado || [], [data])
  const porTipoDoc = useMemo(() => data?.por_tipo_documento || [], [data])
  const porTipoPdf = useMemo(() => data?.por_tipo_pdf || [], [data])
  const porIntentos = useMemo(() => data?.por_intentos || [], [data])
  const bouncesTipo = useMemo(() => data?.bounces?.por_tipo || [], [data])
  const tendenciaEnvios = useMemo(() => data?.tendencia?.envios_por_dia || [], [data])
  const tendenciaEngagement = useMemo(() => data?.tendencia?.engagement_por_dia || [], [data])
  const performance = useMemo(() => data?.performance || [], [data])
  const topSucursales = useMemo(() => data?.top_sucursales || [], [data])
  const topProblematicos = useMemo(() => data?.top_destinatarios_problematicos || [], [data])
  const topEngagement = useMemo(() => data?.top_destinatarios_engagement || [], [data])
  const erroresEtapa = useMemo(() => data?.errores?.por_etapa || [], [data])
  const erroresRecientes = useMemo(() => data?.errores?.recientes || [], [data])

  // ───────── Empty / sin empresa ─────────

  if (!nitemisor) {
    return (
      <div style={{ background: "#fff", borderRadius: 14, padding: 60, textAlign: "center", border: "1px dashed #cbd5e1" }}>
        <i className="mdi mdi-domain-off" style={{ fontSize: 56, color: "#cbd5e1" }} />
        <h5 className="mt-3" style={{ color: "#0f172a" }}>Selecciona una empresa</h5>
        <p style={{ color: "#64748b", maxWidth: 420, margin: "8px auto 0" }}>
          Usa el selector <strong>“Seleccionar empresa”</strong> en la parte superior derecha para ver el análisis de envíos de correo.
        </p>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header con filtros */}
      <div style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
          <div>
            <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
              Envío de correos
            </div>
            <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#0f172a" }}>
              Análisis de envíos
            </h3>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              <i className="mdi mdi-domain me-1 text-primary" />
              <strong>{emisorInfo.nombre_emisor || empresaNombre}</strong> · NIT {nitemisor}
            </div>
            {(emisorInfo.primer_envio_at || emisorInfo.ultimo_envio_at) && (
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                {emisorInfo.primer_envio_at && <>Primer envío: <strong>{formatFechaHora(emisorInfo.primer_envio_at)}</strong></>}
                {emisorInfo.primer_envio_at && emisorInfo.ultimo_envio_at && <> · </>}
                {emisorInfo.ultimo_envio_at && <>Último: <strong>{formatFechaHora(emisorInfo.ultimo_envio_at)}</strong></>}
              </div>
            )}
          </div>
        </div>
        <HeaderFiltros
          modo={modo} setModo={setModo}
          year={year} setYear={setYear} month={month} setMonth={setMonth}
          fechaInicio={fechaInicio} setFechaInicio={setFechaInicio}
          fechaFin={fechaFin} setFechaFin={setFechaFin}
          loading={loading}
          onConsultar={cargar}
          puedeConsultar={puedeConsultar}
        />
      </div>

      {error && (
        <div style={{
          padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca",
          color: "#991b1b", borderRadius: 10,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <i className="mdi mdi-alert-circle" style={{ fontSize: 20 }} />
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger ms-auto"
            onClick={cargar} disabled={loading}>Reintentar</button>
        </div>
      )}

      {!data && loading && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 80, textAlign: "center", color: "#64748b", border: "1px solid #e2e8f0" }}>
          <Spinner color="primary" />
          <div className="mt-2">Cargando análisis…</div>
        </div>
      )}

      {!data && !loading && !error && !consultado && (
        <div style={{ background: "#fff", borderRadius: 14, padding: "60px 40px", textAlign: "center", border: "1px dashed #cbd5e1" }}>
          <i className="mdi mdi-email-search-outline" style={{ fontSize: 56, color: "#cbd5e1" }} />
          <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>
            Configura el período y consulta
          </h5>
          <p style={{ color: "#64748b", maxWidth: 460, margin: "8px auto 0", fontSize: 14 }}>
            Indica mes/año o rango de fechas y pulsa <strong>Consultar</strong> para ver el análisis de envíos de correo de <strong>{empresaNombre}</strong>.
          </p>
        </div>
      )}

      {data && (
        <>
          {/* ─── Tarjetas de KPIs principales ─── */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <StatCard icon="mdi-email" label="Total correos"
              value={formatNumber(tarjetas.total_correos)} accent="#3b82f6"
              sub={tarjetas.destinatarios_intentados != null
                ? `${formatNumber(tarjetas.destinatarios_intentados)} destinatarios intentados` : ""} />
            <StatCard icon="mdi-check-circle" label="Destinatarios OK"
              value={formatNumber(tarjetas.destinatarios_ok)} accent="#10b981"
              sub={tarjetas.destinatarios_fail != null ? `${formatNumber(tarjetas.destinatarios_fail)} fallidos` : ""} />
            <StatCard icon="mdi-percent" label="Tasa entrega"
              value={formatPercent(tarjetas.tasa_entrega_pct)} accent="#06b6d4" />
            <StatCard icon="mdi-clock-outline" label="Pendientes"
              value={formatNumber(tarjetas.pendientes_actuales)} accent="#f59e0b"
              sub={tarjetas.promedio_intentos != null ? `prom ${formatNumber(tarjetas.promedio_intentos, 1)} intentos` : "en cola"} />
          </div>

          {/* ─── Engagement ─── */}
          {engagement && (engagement.tasa_apertura_pct != null || engagement.tasa_click_pct != null || engagement.ctr_pct != null) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              <StatCard icon="mdi-email-open" label="Tasa apertura"
                value={formatPercent(engagement.tasa_apertura_pct)} accent="#10b981"
                sub={engagement.opens_totales != null ? `${formatNumber(engagement.opens_totales)} opens totales` : "destinatarios que abrieron"} />
              <StatCard icon="mdi-cursor-pointer" label="Tasa click"
                value={formatPercent(engagement.tasa_click_pct)} accent="#8b5cf6"
                sub={engagement.clicks_totales != null ? `${formatNumber(engagement.clicks_totales)} clicks totales` : "destinatarios que clickearon"} />
              <StatCard icon="mdi-link-variant" label="CTR"
                value={formatPercent(engagement.ctr_pct)} accent="#ec4899"
                sub="click-through ratio" />
            </div>
          )}

          {/* ─── Eventos SES agregados (nuevo bloque) ─── */}
          {eventosSes && Object.keys(eventosSes).length > 0 && (
            <SectionCard title="Eventos SES agregados" icon="mdi-cloud-outline">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                {[
                  { label: "Sends",         value: eventosSes.sends,         color: "#3b82f6", icon: "mdi-send" },
                  { label: "Deliveries",    value: eventosSes.deliveries,    color: "#10b981", icon: "mdi-check-circle" },
                  { label: "Opens",         value: eventosSes.opens,         color: "#06b6d4", icon: "mdi-email-open" },
                  { label: "Clicks",        value: eventosSes.clicks,        color: "#8b5cf6", icon: "mdi-cursor-pointer" },
                  { label: "Bounces",       value: eventosSes.bounces,       color: "#f59e0b", icon: "mdi-alert" },
                  { label: "Complaints",    value: eventosSes.complaints,    color: "#dc2626", icon: "mdi-flag" },
                  { label: "Delays",        value: eventosSes.delays,        color: "#64748b", icon: "mdi-timer-sand" },
                ].map(m => (
                  <div key={m.label} style={{
                    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
                    padding: "10px 12px", textAlign: "center",
                  }}>
                    <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>
                      <i className={`mdi ${m.icon} me-1`} style={{ color: m.color }} />
                      {m.label}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: m.color, marginTop: 2 }}>
                      {formatNumber(m.value)}
                    </div>
                  </div>
                ))}
              </div>
              {(eventosSes.correos_unicos_entregados != null || eventosSes.destinatarios_que_abrieron != null) && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginTop: 10 }}>
                  {[
                    { label: "Correos únicos entregados", value: eventosSes.correos_unicos_entregados },
                    { label: "Correos únicos abiertos",   value: eventosSes.correos_unicos_abiertos },
                    { label: "Correos únicos c/click",    value: eventosSes.correos_unicos_con_click },
                    { label: "Destinatarios que abrieron",   value: eventosSes.destinatarios_que_abrieron },
                    { label: "Destinatarios que clickearon", value: eventosSes.destinatarios_que_clickearon },
                  ].filter(m => m.value != null).map(m => (
                    <div key={m.label} style={{
                      background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8,
                      padding: "6px 10px", fontSize: 12,
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}>
                      <span style={{ color: "#475569" }}>{m.label}</span>
                      <strong style={{ color: "#0f172a" }}>{formatNumber(m.value)}</strong>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          )}

          {/* ─── Distribuciones (donut + bars + bars) ─── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
            {/* Por estado */}
            {porEstado.length > 0 && (
              <SectionCard title="Distribución por estado" icon="mdi-chart-donut">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={porEstado} dataKey="cantidad" nameKey="estado"
                      innerRadius={55} outerRadius={90} paddingAngle={2}>
                      {porEstado.map((e, i) => <Cell key={i} fill={estadoConfig(e.estado).color || colorPara(i)} />)}
                    </Pie>
                    <Tooltip formatter={(v, _, p) => [formatNumber(v), estadoConfig(p.payload.estado).label]} />
                    <Legend wrapperStyle={{ fontSize: 11 }}
                      formatter={(value) => estadoConfig(value).label} />
                  </PieChart>
                </ResponsiveContainer>
              </SectionCard>
            )}

            {/* Por tipo documento */}
            {porTipoDoc.length > 0 && (
              <SectionCard title="Por tipo de documento" icon="mdi-chart-bar">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={porTipoDoc} layout="vertical" margin={{ left: 30, right: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="tipo_documento" tick={{ fontSize: 11 }} width={120} />
                    <Tooltip formatter={v => formatNumber(v)} />
                    <Bar dataKey="cantidad" fill="#3b82f6" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </SectionCard>
            )}

            {/* Por tipo PDF */}
            {porTipoPdf.length > 0 && (
              <SectionCard title="Por tipo de PDF" icon="mdi-chart-bar">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={porTipoPdf}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="tipopdf" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={v => formatNumber(v)} />
                    <Bar dataKey="cantidad" fill="#10b981" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </SectionCard>
            )}

            {/* Histograma intentos */}
            {porIntentos.length > 0 && (
              <SectionCard title="Distribución por número de intentos" icon="mdi-chart-histogram">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={porIntentos}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="intentos" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={v => formatNumber(v)} labelFormatter={l => `${l} intento(s)`} />
                    <Bar dataKey="cantidad_correos" fill="#f59e0b" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </SectionCard>
            )}

            {/* Bounces por tipo (con origen) */}
            {bouncesTipo.length > 0 && (
              <SectionCard title="Bounces por tipo" icon="mdi-alert-octagon-outline">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={bouncesTipo} dataKey="cantidad" nameKey="bounce_type"
                      innerRadius={55} outerRadius={90} paddingAngle={2}>
                      {bouncesTipo.map((b, i) => <Cell key={i} fill={bounceConfig(b.bounce_type).color || colorPara(i)} />)}
                    </Pie>
                    <Tooltip formatter={(v, _, p) => [
                      formatNumber(v),
                      `${bounceConfig(p.payload.bounce_type).label}${p.payload.origen ? ` · ${p.payload.origen}` : ""}`,
                    ]} />
                    <Legend wrapperStyle={{ fontSize: 11 }}
                      formatter={(_, entry) => `${bounceConfig(entry.payload.bounce_type).label}${entry.payload.origen ? ` (${entry.payload.origen})` : ""}`} />
                  </PieChart>
                </ResponsiveContainer>
              </SectionCard>
            )}
          </div>

          {/* ─── Tendencias ─── */}
          {(tendenciaEnvios.length > 0 || tendenciaEngagement.length > 0) && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 12 }}>
              {tendenciaEnvios.length > 0 && (
                <SectionCard title="Tendencia de envíos por día" icon="mdi-chart-line">
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={tendenciaEnvios}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="dia" tick={{ fontSize: 11 }} tickFormatter={formatFecha} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip
                        labelFormatter={l => formatFecha(l)}
                        formatter={v => formatNumber(v)}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="enviados" name="Enviados" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="fallidos" name="Fallidos" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="omitidos" name="Omitidos" stroke="#94a3b8" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </SectionCard>
              )}
              {tendenciaEngagement.length > 0 && (
                <SectionCard title="Engagement por día" icon="mdi-chart-line">
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={tendenciaEngagement}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="dia" tick={{ fontSize: 11 }} tickFormatter={formatFecha} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip
                        labelFormatter={l => formatFecha(l)}
                        formatter={v => formatNumber(v)}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="deliveries" name="Entregados" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="opens"      name="Aperturas" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="clicks"     name="Clicks"    stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="bounces"    name="Bounces"   stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </SectionCard>
              )}
            </div>
          )}

          {/* ─── Performance por tipo PDF ─── */}
          {performance.length > 0 && (
            <SectionCard title="Performance por tipo de PDF" icon="mdi-speedometer">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>Tipo PDF</th>
                      <th style={{ ...th, textAlign: "right" }}>Cantidad</th>
                      <th style={{ ...th, textAlign: "right" }}>Avg total (ms)</th>
                      <th style={{ ...th, textAlign: "right" }}>P50 (ms)</th>
                      <th style={{ ...th, textAlign: "right" }}>P90 (ms)</th>
                      <th style={{ ...th, textAlign: "right" }}>P99 (ms)</th>
                      <th style={{ ...th, textAlign: "right" }}>Avg download</th>
                      <th style={{ ...th, textAlign: "right" }}>Avg PDF</th>
                      <th style={{ ...th, textAlign: "right" }}>Avg email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {performance.map((p, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                        <td style={{ ...td, fontWeight: 600 }}>{p.tipopdf || "—"}</td>
                        <td style={{ ...td, textAlign: "right" }}>{formatNumber(p.cantidad)}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{formatNumber(p.avg_total_ms, 0)}</td>
                        <td style={{ ...td, textAlign: "right" }}>{formatNumber(p.p50_total_ms, 0)}</td>
                        <td style={{ ...td, textAlign: "right" }}>{formatNumber(p.p90_total_ms, 0)}</td>
                        <td style={{ ...td, textAlign: "right", color: "#dc2626" }}>{formatNumber(p.p99_total_ms, 0)}</td>
                        <td style={{ ...td, textAlign: "right", color: "#64748b", fontSize: 12 }}>{formatNumber(p.avg_download_ms, 0)}</td>
                        <td style={{ ...td, textAlign: "right", color: "#64748b", fontSize: 12 }}>{formatNumber(p.avg_pdf_ms, 0)}</td>
                        <td style={{ ...td, textAlign: "right", color: "#64748b", fontSize: 12 }}>{formatNumber(p.avg_email_ms, 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          )}

          {/* ─── Tablas top (sucursales + problemáticos + engagement) ─── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 12 }}>
            {topSucursales.length > 0 && (
              <SectionCard title="Top sucursales" icon="mdi-source-branch">
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={th}>Sucursal</th>
                        <th style={{ ...th, textAlign: "right" }}>Correos</th>
                        <th style={{ ...th, textAlign: "right" }}>Enviados</th>
                        <th style={{ ...th, textAlign: "right" }}>Fallidos</th>
                        <th style={{ ...th, textAlign: "right" }}>% OK</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topSucursales.map((s, i) => {
                        // Calculamos la tasa OK localmente porque el back no la manda
                        const cantidad = Number(s.cantidad) || 0
                        const enviados = Number(s.enviados) || 0
                        const tasaOk = cantidad > 0 ? (enviados / cantidad) * 100 : 0
                        return (
                          <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                            <td style={td}>{s.sucursal || "—"}</td>
                            <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{formatNumber(cantidad)}</td>
                            <td style={{ ...td, textAlign: "right", color: "#10b981" }}>{formatNumber(enviados)}</td>
                            <td style={{ ...td, textAlign: "right", color: "#dc2626" }}>{formatNumber(s.fallidos)}</td>
                            <td style={{ ...td, textAlign: "right" }}>
                              <span style={{
                                padding: "1px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                                background: tasaOk >= 90 ? "#dcfce7" : tasaOk >= 70 ? "#fef9c3" : "#fee2e2",
                                color: tasaOk >= 90 ? "#166534" : tasaOk >= 70 ? "#854d0e" : "#991b1b",
                              }}>{formatPercent(tasaOk, 1)}</span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            )}

            {topEngagement.length > 0 && (
              <SectionCard title="Top destinatarios — engagement" icon="mdi-account-star-outline">
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={th}>Destinatario</th>
                        <th style={{ ...th, textAlign: "right" }}>Aperturas</th>
                        <th style={{ ...th, textAlign: "right" }}>Clicks</th>
                        <th style={{ ...th, textAlign: "right" }}>Correos abiertos</th>
                        <th style={th}>Último evento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topEngagement.map((d, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                          <td style={td}>{d.recipient || "—"}</td>
                          <td style={{ ...td, textAlign: "right", color: "#10b981", fontWeight: 600 }}>
                            {formatNumber(d.opens)}
                          </td>
                          <td style={{ ...td, textAlign: "right", color: "#8b5cf6", fontWeight: 600 }}>
                            {formatNumber(d.clicks)}
                          </td>
                          <td style={{ ...td, textAlign: "right" }}>{formatNumber(d.correos_abiertos)}</td>
                          <td style={{ ...td, fontSize: 11, color: "#64748b", fontFamily: "monospace" }}>
                            {formatFechaHora(d.ultimo_evento_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            )}
          </div>

          {/* ─── Top destinatarios problemáticos ─── */}
          {topProblematicos.length > 0 && (
            <SectionCard title="Destinatarios problemáticos (limpiar BD)" icon="mdi-account-alert-outline">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>Destinatario</th>
                      <th style={{ ...th, textAlign: "right" }}>Rebotes</th>
                      <th style={{ ...th, textAlign: "right" }}>CUFEs afectados</th>
                      <th style={th}>Tipos de bounce</th>
                      <th style={th}>Último rebote</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topProblematicos.map((d, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                        <td style={td}>
                          {d.recipient ? (
                            <span style={{ wordBreak: "break-all" }}>{d.recipient}</span>
                          ) : (
                            <span style={{ color: "#94a3b8", fontStyle: "italic" }}>(sin destinatario)</span>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: "right", color: "#dc2626", fontWeight: 700 }}>
                          {formatNumber(d.rebotes)}
                        </td>
                        <td style={{ ...td, textAlign: "right", color: "#475569" }}>
                          {formatNumber(d.cufes_afectados)}
                        </td>
                        <td style={td}>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                            {(d.tipos_bounce || []).map((bt, idx) => {
                              const cfg = bounceConfig(bt)
                              return (
                                <span key={idx} style={{
                                  padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                                  background: cfg.bg, color: cfg.color,
                                }}>{cfg.label}</span>
                              )
                            })}
                          </div>
                        </td>
                        <td style={{ ...td, fontSize: 12, color: "#64748b", fontFamily: "monospace" }}>
                          {formatFechaHora(d.ultimo_rebote_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          )}

          {/* ─── Errores por etapa + feed reciente ─── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {erroresEtapa.length > 0 && (
              <SectionCard title="Errores por etapa" icon="mdi-bug-outline">
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={th}>Etapa</th>
                        <th style={{ ...th, textAlign: "right" }}>Cantidad</th>
                        <th style={{ ...th, textAlign: "right" }}>CUFEs afectados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {erroresEtapa.map((e, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}
                          title={e.ejemplo || ""}>
                          <td style={td}>
                            <span style={{
                              padding: "1px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                              background: "#fee2e2", color: "#991b1b",
                            }}>{e.error_stage || "—"}</span>
                          </td>
                          <td style={{ ...td, textAlign: "right", fontWeight: 700, color: "#dc2626" }}>
                            {formatNumber(e.cantidad)}
                          </td>
                          <td style={{ ...td, textAlign: "right", color: "#475569" }}>
                            {formatNumber(e.cufes_afectados)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            )}

            {erroresRecientes.length > 0 && (
              <SectionCard title="Errores recientes" icon="mdi-alert-outline">
                <div style={{ maxHeight: 360, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                  {erroresRecientes.slice(0, 20).map((e, i) => (
                    <div key={i} style={{
                      background: "#fef2f2", border: "1px solid #fecaca",
                      borderLeft: "3px solid #dc2626",
                      borderRadius: 6, padding: "8px 10px", fontSize: 11,
                    }}>
                      <div className="d-flex justify-content-between align-items-baseline mb-1 flex-wrap gap-1">
                        <div>
                          <strong style={{ color: "#991b1b" }}>{e.error_stage || "—"}</strong>
                          {e.prefi_folio && (
                            <span style={{
                              marginLeft: 6, padding: "1px 6px", borderRadius: 4,
                              background: "#fff", color: "#475569",
                              fontSize: 10, fontFamily: "monospace", fontWeight: 700,
                            }}>{e.prefi_folio}</span>
                          )}
                        </div>
                        <span style={{ color: "#64748b", fontFamily: "monospace", fontSize: 10 }}>
                          {formatFechaHora(e.event_time)}
                        </span>
                      </div>
                      {e.recipient && (
                        <div style={{ color: "#475569", marginBottom: 2, wordBreak: "break-all" }}>
                          <i className="mdi mdi-email-outline me-1" />{e.recipient}
                        </div>
                      )}
                      <div style={{ color: "#7f1d1d", wordBreak: "break-word" }}>
                        {e.error_message || "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}
          </div>
        </>
      )}
    </div>
  )
}

Analisis.propTypes = {
  empresaSeleccionada: PropTypes.object,
  user: PropTypes.object,
  setBreadcrumbItems: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  empresaSeleccionada: state.Login.empresaSeleccionada,
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(Analisis)
